import * as React from 'react';
import { atom, useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { FormLabelWrapper, PhoneInput, isValidPhoneNumber } from '@restworld/ui-ds-base';

import { useEmployerPhoneNumberUpdateMutation } from './use-employer-user-information';
import { IFormModalWrapper, ModalToggleWrapper } from './form-modal-wrapper';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IUpdatePhoneNumberModalProps {}

export const phoneNumberSentFlagAtom = atomWithStorage<boolean>(
  'phoneNumberSentFlag',
  false,
  createJSONStorage<boolean>(() => localStorage)
);

export const phoneNumberSentAtom = atomWithStorage<string>(
  'phoneNumberSent',
  '',
  createJSONStorage<string>(() => localStorage)
);

export const phoneNumberVerificationDateNowAtom = atomWithStorage<number>(
  'phoneNumberVerificationDateNow',
  null,
  createJSONStorage<number>(() => localStorage)
);

export const phoneNumberVerificationModalVisibleAtom = atom<boolean>(false);

const UpdatePhoneNumberModal: React.FunctionComponent<IUpdatePhoneNumberModalProps> = (props) => {
  const [phoneNumberVerificationModalVisible, phoneNumberVerificationModalVisibleToggler] = useAtom(
    phoneNumberVerificationModalVisibleAtom
  );
  const methods = useForm<{
    phoneNumber?: string;
  }>({
    defaultValues: {
      phoneNumber: '',
    },
  });

  const {
    reset,
    control,
    formState: { errors },
    handleSubmit,
    setError,
  } = methods;

  const handleReset = React.useCallback(() => {
    reset({ phoneNumber: '' });
    phoneNumberVerificationModalVisibleToggler(false);
  }, [phoneNumberVerificationModalVisibleToggler, reset]);
  const { t } = useTranslation('user-settings');

  const { mutate, isPending, isError, error, isSuccess } = useEmployerPhoneNumberUpdateMutation();
  const onSubmit: SubmitHandler<{ phoneNumber: string }> = React.useCallback((data) => mutate(data), [mutate]);

  const modalProps: IFormModalWrapper = React.useMemo(() => {
    return {
      modalVisible: phoneNumberVerificationModalVisible,
      onModalClose: handleReset,
      title: t('update_phone_number'),
      subTitle: t('we_send_a_verification_code'),
      submitHandlerProps: {
        onClick: handleSubmit(onSubmit),
        title: t('send_verification_code'),
        loading: isPending,
      },
      onCancelHandler: handleReset,
    };
  }, [handleReset, handleSubmit, isPending, onSubmit, phoneNumberVerificationModalVisible, t]);

  React.useEffect(() => {
    if (isError) {
      setError('phoneNumber', {
        message: error?.['data']?.['error'] || t('something_went_wrong'),
      });
    }
  }, [error, isError, setError, t]);

  React.useEffect(() => {
    if (isSuccess) handleReset();
  }, [handleReset, isSuccess]);

  return (
    <ModalToggleWrapper {...modalProps}>
      <FormLabelWrapper label={t('phone_number')} isFullWidth id="phoneNumber">
        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            validate: (phoneNumber) => isValidPhoneNumber(phoneNumber) || t('insert_valid_phone_number'),
          }}
          render={({ field }) => (
            <PhoneInput
              priorityCountries={['IT', 'FR', 'ES']}
              inputContainerStyles={['border-neutral-100 border']}
              variant={errors['phoneNumber'] ? 'error' : 'primary'}
              supportText={errors['phoneNumber']?.message}
              {...field}
            />
          )}
        />
      </FormLabelWrapper>
    </ModalToggleWrapper>
  );
};

export default UpdatePhoneNumberModal;
